// src/components/RelatedProducts.jsx (Improved Version)

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import ProductCard from './NewProductCard';
import { ProductCardSkeleton } from './NewProductCard';
import { Package } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';

const RelatedProducts = ({ productIds = [], currentProductId, taxonomy }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (productIds.length > 0) {
      fetchRelatedProducts();
    }
  }, [productIds, currentProductId]);

  const fetchRelatedProducts = async () => {
    setLoading(true);
    
    try {
      // Filter out current product and limit to 5 IDs
      const filteredIds = productIds
        .filter(id => id !== currentProductId)
        .slice(0, 5);
      
      if (filteredIds.length === 0) {
        setProducts([]);
        return;
      }
      
      // Use batch endpoint for better performance
      const response = await fetch(
        `${process.env.REACT_APP_WP_BASE}/custom/v1/products/batch?ids=${filteredIds.join(',')}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Failed to fetch related products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    // Navigate to product detail page with GS prefix and slug
    navigate(`/product/${product.slug || 'product'}/gs${product.id}`);
    // Scroll to top
    window.scrollTo(0, 0);
  };

  // Don't render if no products
  if (!productIds.length || productIds.length === 0) return null;
  return (
    <>
      <style>{`
        /* Mobile Slider - Visible only on mobile */
        .related-products-mobile-slider {
          display: block;
        }

        .related-products-mobile-slider .swiper {
          overflow: hidden;
          padding: 1rem 0;
        }

        .related-products-mobile-slider .swiper-wrapper {
          align-items: stretch;
        }

        .related-products-mobile-slider .swiper-slide {
          height: auto;
          display: flex;
        }

        .related-products-card-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .related-products-card-wrapper > div {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        @media (min-width: 640px) {
          .related-products-mobile-slider {
            display: none !important;
          }
        }

        /* Desktop Grid - Hidden on mobile */
        .related-products-desktop-grid {
          display: none !important;
        }

        @media (min-width: 640px) {
          .related-products-desktop-grid {
            display: grid !important;
          }
        }

        /* Swiper Navigation Customization */
        .related-products-mobile-slider .swiper-button-next,
        .related-products-mobile-slider .swiper-button-prev {
          color: #f7971e;
          background: rgba(255, 255, 255, 0.9);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .related-products-mobile-slider .swiper-button-next::after,
        .related-products-mobile-slider .swiper-button-prev::after {
          font-size: 1.25rem;
          font-weight: bold;
        }
      `}</style>

      <section className="">
        <div className="flex justify-between items-center mb-8">
        <div className='inline-flex items-center space-x-3'>
            <Package className="w-6 h-6 font-display font-bold text-black" />
                <h2 className="text-4xl font-normal text-black">
                  Bestselling <span className='font-semibold'>Related Products</span>
                </h2>
        </div>
        
          <button 
            onClick={() => navigate(`/products?taxonomy=${taxonomy}`)}
            className="listingBtn text-base font-semibold gradient-text"
          >
            View more Listing <span className='chevronRight'></span>
          </button>
        
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <ProductCardSkeleton key={i} viewMode="grid" />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          {/* Mobile Slider */}
          <div className="related-products-mobile-slider">
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={16}
              slidesPerView={1.1}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              breakpoints={{
                480: {
                  slidesPerView: 1.2,
                  spaceBetween: 20
                }
              }}
            >
              {products.slice(0, 3).map(product => (
                <SwiperSlide key={product.id}>
                  <div className="related-products-card-wrapper">
                    <ProductCard
                      product={product}
                      taxonomy={taxonomy}
                      viewMode="grid"
                      onClick={() => handleProductClick(product)}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Desktop Grid */}
          <div className="related-products-desktop-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.slice(0, 3).map(product => (
              <div key={product.id}>
                <ProductCard
                  product={product}
                  taxonomy={taxonomy}
                  viewMode="grid"
                  onClick={() => handleProductClick(product)}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-neutral-500 text-center py-8">No related products found</p>
      )}
    </section>
    </>
  );
};

export default RelatedProducts;